import { useMemo } from 'react';
import type {
  ProcessEventNode,
  ProcessStepAssignmentStatus,
  ProcessStepRecording,
  ProcessWithSteps
} from '@bliks-io/contracts';
import { hasRenderableEventContent, type OpenAssignmentPlaceholder } from '@bliks-io/ui';

export interface UseProcessFlowDataInput {
  /**
   * Backing process shape. The hook passes this through to the flow renderer
   * unchanged — it never mutates `processStepRecordings`. The editor builds
   * this from draft steps; the preview passes the published process.
   */
  process: ProcessWithSteps | null;
  /**
   * Placeholder assignments already adapted to the wire shape the flow builder
   * expects. Draft editor supplies draft assignments (`kind: 'assignment'`);
   * the preview supplies in-flight invitations from the published process.
   */
  openAssignments?: OpenAssignmentPlaceholder[];
  /**
   * Unified event nodes from the published process (`events[]` on
   * show-process, ADR 0003).
   */
  events?: ProcessEventNode[];
  /** Persisted swimlane order (lane keys). */
  swimlaneOrder?: string[];
  /**
   * Preview-only: the in-flight `ProcessStepRecording` the current user is
   * still editing. Surfaced as a synthetic placeholder (kind: open-assignment)
   * so preview and editor render the same node type. Skipped if its id is
   * already present in `process.processStepRecordings`.
   */
  currentRecording?: ProcessStepRecording | null;
  /**
   * Preview-only: known assignment status for `currentRecording`. When
   * `pending-unclear`, the builder renders the placeholder as an amber "?"
   * unclear-variant node.
   */
  currentRecordingStatus?: ProcessStepAssignmentStatus;
}

export interface UseProcessFlowDataResult {
  processForFlow: ProcessWithSteps | null;
  openAssignments: OpenAssignmentPlaceholder[];
  events?: ProcessEventNode[];
  swimlaneOrder?: string[];
  /**
   * True when the flow has any renderable content: a start event, a recorded
   * step, or a placeholder assignment. Consumers gate the empty-state vs flow
   * rendering on this.
   */
  hasFlowContent: boolean;
}

/**
 * Shared flow-data preparation for both the draft editor and the recording
 * preview dialog. Centralizes two invariants:
 *
 *  1. Placeholder assignments are never merged into `processStepRecordings`.
 *     Preview previously appended `currentRecording` into that array, which
 *     rendered the in-flight step as a full activity node AND (when it was
 *     the first step) produced a duplicate START edge.
 *
 *  2. `hasFlowContent` captures "anything to render" — start event alone is
 *     enough, placeholders alone are enough. Empty-state should only appear
 *     when the canvas is truly empty.
 */
export function useProcessFlowData(input: UseProcessFlowDataInput): UseProcessFlowDataResult {
  const {
    process,
    openAssignments,
    events,
    swimlaneOrder,
    currentRecording,
    currentRecordingStatus
  } = input;

  const savedStepIds = useMemo(() => {
    const ids = new Set<string>();
    for (const step of process?.processStepRecordings ?? []) {
      ids.add(step._id);
    }
    return ids;
  }, [process?.processStepRecordings]);

  const effectiveOpenAssignments = useMemo<OpenAssignmentPlaceholder[]>(() => {
    const base = openAssignments ?? EMPTY_ASSIGNMENTS;
    if (!currentRecording) return base;
    if (savedStepIds.has(currentRecording._id)) return base;
    const synthesized = buildPlaceholderFromRecording(
      currentRecording,
      currentRecordingStatus ?? 'pending'
    );
    if (!synthesized) return base;
    return [...base, synthesized];
  }, [openAssignments, currentRecording, currentRecordingStatus, savedStepIds]);

  const hasFlowContent = useMemo(() => {
    const hasSteps = (process?.processStepRecordings?.length ?? 0) > 0;
    const hasPlaceholders = effectiveOpenAssignments.length > 0;
    const hasEventNodes = hasRenderableEventContent(events);
    return hasSteps || hasPlaceholders || hasEventNodes;
  }, [process, effectiveOpenAssignments, events]);

  return {
    processForFlow: process,
    openAssignments: effectiveOpenAssignments,
    events,
    swimlaneOrder,
    hasFlowContent
  };
}

const EMPTY_ASSIGNMENTS: OpenAssignmentPlaceholder[] = [];

/**
 * Project an in-flight `ProcessStepRecording` into the placeholder wire shape.
 * Returns `null` when the recording isn't renderable as a placeholder yet —
 * no assignee picked, or the assignee is still a bare id (unpopulated).
 *
 * Inputs travel verbatim: start-anchored recordings carry an
 * eventId-stamped start input since the issue-11 flip, which the builder
 * renders as the start edge.
 */
function buildPlaceholderFromRecording(
  rec: ProcessStepRecording,
  status: ProcessStepAssignmentStatus
): OpenAssignmentPlaceholder | null {
  if (!rec.assignedTo || !rec.assignedToModel) return null;
  // `rec.assignedTo` is an EntityOrSuggestionRef — only resolved entity refs
  // can populate a placeholder, and even then the ref carries no populated
  // user/team payload (the flow builder needs that). Until the caller passes
  // populated entities alongside the recording, we can't synthesize the
  // placeholder. Returning null leaves the placeholder list empty for this
  // recording, matching the previous "unpopulated assignedTo" fallback.
  if (rec.assignedTo.type !== 'entity') return null;

  // Event inputs ride along (a converted step keeps its start-event edges)
  // so the preview renders the same event nodes the editor does.
  const inputs: OpenAssignmentPlaceholder['inputs'] = rec.inputs ?? [];

  // Without a populated assignee payload, we can't render the assignee chip;
  // skip rather than synthesize a stub that would break downstream consumers.
  // Drop suppressed in a follow-up once populated assignedTo hand-off is wired.
  return {
    kind: 'open-assignment',
    _id: rec._id,
    stableStepId: rec._id,
    assignedTo: { _id: rec.assignedTo.id } as OpenAssignmentPlaceholder['assignedTo'],
    assignedToModel: rec.assignedToModel,
    inputs,
    status
  };
}
